import React, { useState } from "react";
import {
	X,
	Clock,
	MapPin,
	Video,
	Users,
	Copy,
	Check,
	Edit2,
	Trash2,
	AlertCircle,
	ExternalLink,
} from "lucide-react";
import { format } from "date-fns";
import EditEvent from "./EditEvent";
import DOMPurify from "dompurify";
import ViewAllParticipantsModal from "./ViewAllParticipantsModal";

const EventPopover = ({
	isOpen,
	onClose,
	event,
	onUpdateEvent,
	onDeleteEvent,
	canEdit = true,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [copiedField, setCopiedField] = useState(null);
	const [showAllParticipants, setShowAllParticipants] = useState(false);

	const handleCopy = (text, field) => {
		navigator.clipboard.writeText(text);
		setCopiedField(field);
		setTimeout(() => setCopiedField(null), 2000);
	};

	const truncateText = (text, maxLength) => {
		if (text.length <= maxLength) return text;
		return text.substring(0, maxLength) + "...";
	};

	const getMeetingLink = () => {
		// Handle Google Meet
		if (event.conferenceData?.conferenceSolution?.name === "Google Meet") {
			const entryPoint = event.conferenceData.entryPoints?.find(
				(ep) => ep.entryPointType === "video"
			);
			if (entryPoint?.uri) {
				return {
					url: entryPoint.uri,
					type: "Google Meet",
					icon: "google-meet",
				};
			}
		}

		// Handle Zoom
		if (
			event.conferenceData?.conferenceSolution?.name === "Zoom Meeting" ||
			event.conferenceData?.entryPoints?.some((ep) =>
				ep.label?.includes("zoom.us")
			)
		) {
			const videoEntryPoint = event.conferenceData.entryPoints?.find(
				(ep) => ep.entryPointType === "video"
			);
			if (videoEntryPoint?.uri) {
				return {
					url: videoEntryPoint.uri,
					type: "Zoom",
					icon: "zoom",
					meetingId:
						videoEntryPoint.meetingCode || event.conferenceData.conferenceId,
					passcode: videoEntryPoint.passcode,
				};
			}
		}

		// Fallback for simple location-based meeting links
		if (
			event.location?.includes("zoom.us") ||
			event.location?.includes("meet.google.com") ||
			event.location?.includes("teams.microsoft.com")
		) {
			return {
				url: event.location,
				type: event.location.includes("zoom.us")
					? "Zoom"
					: event.location.includes("meet.google.com")
					? "Google Meet"
					: "Teams",
			};
		}

		return null;
	};

	const getInitials = (email) => {
		return email.split("@")[0].charAt(0).toUpperCase();
	};

	const containsHTML = (str) => {
		const doc = new DOMParser().parseFromString(str, "text/html");
		return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
	};

	const renderDescription = (description) => {
		if (!description) return null;

		if (containsHTML(description)) {
			return (
				<div
					className="mt-4 text-sm description-content"
					dangerouslySetInnerHTML={{
						__html: DOMPurify.sanitize(description, {
							ALLOWED_TAGS: [
								"b",
								"i",
								"em",
								"strong",
								"u",
								"ul",
								"ol",
								"li",
								"br",
								"p",
								"a",
								"span",
								"div",
								"strike",
								"del",
								"h1",
								"h2",
								"h3",
								"h4",
								"h5",
								"h6",
							],
							ALLOWED_ATTR: ["href", "target", "rel"],
							ADD_ATTR: ["target"],
						}),
					}}
				/>
			);
		}

		return (
			<div className="mt-4 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
				{description}
			</div>
		);
	};

	const handleDelete = async () => {
		try {
			await onDeleteEvent(event.id);
			setShowDeleteDialog(false);
			onClose();
		} catch (error) {
			console.error("Failed to delete event:", error);
		}
	};

	if (!isOpen || !event) return null;

	const meetingLink = getMeetingLink();

	const ViewMode = () => (
		<div className="flex flex-col h-full">
			<div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-600/20 sticky top-0 bg-white dark:bg-gray-900">
				<div className="flex-1 pr-4">
					<h2
						className={`font-semibold text-gray-900 dark:text-gray-100 ${
							event.title.length > 50
								? "text-sm"
								: event.title.length > 30
								? "text-base"
								: "text-lg"
						}`}
					>
						{event.title}
					</h2>
					<div className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
						<Clock className="w-4 h-4 mr-2 flex-shrink-0" />
						<span>
							{format(event.start, "h:mm a")} - {format(event.end, "h:mm a")}
						</span>
					</div>
				</div>
				{canEdit && (
					<div className="flex gap-1 flex-shrink-0">
						<button
							onClick={() => setIsEditing(true)}
							className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
						>
							<Edit2 className="w-4 h-4" />
						</button>
						<button
							onClick={() => setShowDeleteDialog(true)}
							className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
						>
							<Trash2 className="w-4 h-4" />
						</button>
						<button
							onClick={onClose}
							className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
						>
							<X className="w-4 h-4" />
						</button>
					</div>
				)}
			</div>

			<div className="p-4 space-y-4 flex-1 overflow-y-auto break-words whitespace-normal">
				<div className="text-sm text-gray-500 dark:text-gray-400">
					{format(event.start, "EEEE, MMMM d, yyyy")}
				</div>

				{meetingLink && (
					<div className="space-y-2">
						<button
							onClick={() => window.open(meetingLink.url, "_blank")}
							className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
						>
							<Video className="w-4 h-4" />
							<span>Join {meetingLink.type}</span>
							<ExternalLink className="w-4 h-4" />
						</button>

						{meetingLink.type === "Zoom" && meetingLink.meetingId && (
							<div className="text-sm space-y-1 pl-2">
								<div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
									<span>Meeting ID: {meetingLink.meetingId}</span>
									<button
										onClick={() =>
											handleCopy(meetingLink.meetingId, "meetingId")
										}
										className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
									>
										{copiedField === "meetingId" ? (
											<Check className="w-3 h-3 text-green-500" />
										) : (
											<Copy className="w-3 h-3" />
										)}
									</button>
								</div>
								{meetingLink.passcode && (
									<div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
										<span>Passcode: {meetingLink.passcode}</span>
										<button
											onClick={() =>
												handleCopy(meetingLink.passcode, "passcode")
											}
											className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
										>
											{copiedField === "passcode" ? (
												<Check className="w-3 h-3 text-green-500" />
											) : (
												<Copy className="w-3 h-3" />
											)}
										</button>
									</div>
								)}
							</div>
						)}
					</div>
				)}

				{event.location && !meetingLink?.url.includes(event.location) && (
					<div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
						<MapPin className="w-4 h-4 flex-shrink-0" />
						<span className="text-sm">{event.location}</span>
					</div>
				)}

				{event.attendees && event.attendees.length > 0 && (
					<div className="space-y-2">
						<div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
							<Users className="w-4 h-4 flex-shrink-0" />
							<span className="text-sm">
								{event.attendees.length} participants
							</span>
						</div>
						<div className="pl-6 space-y-2">
							{event.attendees.slice(0, 4).map((attendee, index) => (
								<div key={index} className="flex items-center justify-between">
									<div className="flex items-center gap-2 flex-1 min-w-0">
										<div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
											<span className="text-xs text-gray-600 dark:text-gray-300">
												{getInitials(attendee.email)}
											</span>
										</div>
										<span className="text-sm text-gray-600 dark:text-gray-300 truncate">
											{truncateText(attendee.email, 40)}
										</span>
									</div>
									{attendee.responseStatus && (
										<span
											className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
												attendee.responseStatus === "accepted"
													? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
													: attendee.responseStatus === "declined"
													? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
													: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400"
											}`}
										>
											{attendee.responseStatus === "accepted"
												? "accepted"
												: attendee.responseStatus === "declined"
												? "declined"
												: "pending"}
										</span>
									)}
								</div>
							))}
							{event.attendees.length > 4 && (
								<button
									onClick={() => setShowAllParticipants(true)}
									className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mt-2"
								>
									View all {event.attendees.length} participants...
								</button>
							)}
						</div>
					</div>
				)}

				{renderDescription(event.description)}
			</div>
		</div>
	);

	return (
		<>
			{/* Only render the backdrop and container if we're not editing */}
			{!isEditing ? (
				<>
					<div
						className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[1000]"
						onClick={onClose}
					/>
					<div className="fixed z-[1001] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-y-auto overflow-x-hidden flex flex-col max-h-[60vh] scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar scrollbar-thumb-slate-300 scrollbar-track-slate-100 dark:scrollbar-thumb-slate-800 dark:scrollbar-track-slate-900">
						<ViewMode />
					</div>
				</>
			) : (
				<EditEvent
					event={event}
					onSave={async (updatedEvent) => {
						try {
							await onUpdateEvent(updatedEvent);
							setIsEditing(false);
							onClose(); // Close the popover after successful save
						} catch (error) {
							console.error("Failed to update event:", error);
						}
					}}
					onClose={() => {
						setIsEditing(false);
						onClose(); // Close the popover when canceling edit
					}}
					isOpen={isEditing}
				/>
			)}

			{showDeleteDialog && (
				<div className="fixed inset-0 z-[1010] flex items-center justify-center p-4">
					<div
						className="fixed inset-0 bg-black/50"
						onClick={() => setShowDeleteDialog(false)}
					/>
					<div className="relative bg-white dark:bg-gray-900 rounded-lg p-6 max-w-sm w-full">
						<div className="flex items-start space-x-4">
							<div className="flex-shrink-0">
								<AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
							</div>
							<div>
								<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
									Delete Event
								</h3>
								<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
									Are you sure you want to delete this event? This action cannot
									be undone.
								</p>
								<div className="mt-4 flex space-x-3">
									<button
										onClick={handleDelete}
										className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
									>
										Delete
									</button>
									<button
										onClick={() => setShowDeleteDialog(false)}
										className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
									>
										Cancel
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			<ViewAllParticipantsModal
				isOpen={showAllParticipants}
				onClose={() => setShowAllParticipants(false)}
				attendees={event.attendees || []}
			/>
		</>
	);
};

export default EventPopover;
